import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Edit3,
  Save,
  X,
  RefreshCw,
  Loader2,
  Clock,
  Play,
  Pause,
  Image as ImageIcon,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'shell/Dialog';
import { Clip } from '@/types/clip.types';
import { EpisodeStatus } from '@/types/episode.types';
import { AudioPlayer } from './AudioPlayer';
import { StatusBadge } from './StatusBadge';
import { getFtpStreamUrl } from '@/services/api';
import { cn } from '@/lib/utils';

interface ClipCardProps {
  clip: Clip;
  position: number;
  episodeStatus: EpisodeStatus;
  isFirst: boolean;
  isLast: boolean;
  onReorder: (direction: 'up' | 'down') => void;
  onDelete: () => Promise<void>;
  onRegenerateScript: () => Promise<void>;
  onRegenerateAudio: () => Promise<void>;
  onEditScript: (newScript: string) => Promise<void>;
  isPlaying?: boolean;
  isActiveInPlayAll?: boolean; // True when this clip is the current one in "Play All" mode
  isPlayAllPaused?: boolean; // True when "Play All" is paused (audio should pause but stay mounted)
  onPlayStateChange?: (clipId: string, playing: boolean) => void;
  onEnded?: () => void;
  actionInProgress?: string | null;
  isLocked?: boolean; // When true, disable editing actions but allow audio playback
}

/**
 * Format duration in seconds to MM:SS
 */
function formatDuration(seconds: number | null): string {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Unified ClipCard component for all episode phases.
 * Handles draft through published states with conditional rendering based on data presence.
 */
export const ClipCard: React.FC<ClipCardProps> = ({
  clip,
  position,
  episodeStatus,
  isFirst,
  isLast,
  onReorder,
  onDelete,
  onRegenerateScript,
  onRegenerateAudio,
  onEditScript,
  isPlaying: isPlayingProp = false,
  isActiveInPlayAll = false,
  isPlayAllPaused = false,
  onPlayStateChange,
  onEnded,
  actionInProgress,
  isLocked = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedScript, setEditedScript] = useState(clip.script || '');
  const [showRegenerateScriptDialog, setShowRegenerateScriptDialog] = useState(false);
  const [showRegenerateAudioDialog, setShowRegenerateAudioDialog] = useState(false);
  const [showRegenerateAfterEditDialog, setShowRegenerateAfterEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);

  // Computed feature flags based on episode status and clip data
  const lockedStatuses = [
    EpisodeStatus.MERGING,
    EpisodeStatus.MERGED,
    EpisodeStatus.PUBLISHING,
    EpisodeStatus.PUBLISHED,
  ];
  const canReorder = !lockedStatuses.includes(episodeStatus);

  // Block deletion during active processes and terminal states
  const deleteBlockedStatuses = [
    EpisodeStatus.GENERATING_AUDIO,
    ...lockedStatuses,
  ];
  const canDelete = !deleteBlockedStatuses.includes(episodeStatus);

  // Audio and script availability
  const hasAudio = clip.audioPath && ['audio_ready', 'audio_approved'].includes(clip.status);
  const hasScript = !!clip.script;
  const canEditScript = hasScript && canDelete;

  // Action availability based on clip status
  const canRegenerateScript = ['script_ready', 'script_failed', 'script_queued'].includes(clip.status);
  const canRegenerateAudio = ['audio_ready', 'audio_failed', 'audio_queued'].includes(clip.status);

  // Reset audio state when audioPath changes (e.g., after audio regeneration)
  const prevAudioPathRef = useRef(clip.audioPath);
  useEffect(() => {
    if (clip.audioPath !== prevAudioPathRef.current) {
      prevAudioPathRef.current = clip.audioPath;
      setAudioUrl(null);
      setAudioError(null);
    }
  }, [clip.audioPath]);

  // Pre-load audio URL when hasAudio (not just when expanded)
  useEffect(() => {
    if (hasAudio && !audioUrl && !audioError && !isLoadingAudio) {
      setIsLoadingAudio(true);
      getFtpStreamUrl(clip.audioPath!, clip.companyId)
        .then(setAudioUrl)
        .catch((err) => {
          console.error('Failed to get audio URL:', err);
          setAudioError('Failed to load audio');
        })
        .finally(() => setIsLoadingAudio(false));
    }
  }, [hasAudio, audioUrl, audioError, isLoadingAudio, clip.audioPath, clip.companyId]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handlePlayToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPlayingProp) {
      onPlayStateChange?.(clip.id, false);
    } else {
      onPlayStateChange?.(clip.id, true);
    }
  };

  const handleAudioEnded = () => {
    onPlayStateChange?.(clip.id, false);
    onEnded?.();
  };

  const handleStartEdit = () => {
    setEditedScript(clip.script || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedScript(clip.script || '');
    setIsEditing(false);
  };

  const handleSaveScript = async () => {
    await onEditScript(editedScript);
    setIsEditing(false);
    // After saving, prompt to regenerate audio if audio already exists
    if (hasAudio) {
      setShowRegenerateAfterEditDialog(true);
    }
  };

  const handleRegenerateScriptConfirm = async () => {
    setShowRegenerateScriptDialog(false);
    await onRegenerateScript();
  };

  const handleRegenerateAudioConfirm = async () => {
    setShowRegenerateAudioDialog(false);
    setShowRegenerateAfterEditDialog(false);
    await onRegenerateAudio();
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteDialog(false);
    await onDelete();
  };

  // Track hover state for styling
  const [isHovered, setIsHovered] = useState(false);

  return (
    <>
      <div
        className={cn(
          'grid grid-cols-[auto_1fr_auto] rounded-xl overflow-hidden transition-all duration-200 ease-in-out',
          (isPlayingProp || isActiveInPlayAll) && 'border-cyan-500'
        )}
        style={{
          backgroundColor: (isPlayingProp || isActiveInPlayAll)
            ? 'var(--pm-cyan-bg)'
            : isHovered
              ? 'var(--pm-bg-card-alt)'
              : 'var(--pm-bg-card)',
          border: (isPlayingProp || isActiveInPlayAll)
            ? '1px solid rgb(6, 182, 212)'
            : isHovered
              ? '1px solid var(--pm-border-default)'
              : '1px solid var(--pm-border-default)',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid="clip-card"
      >
        {/* Column 1: Position with reorder arrows */}
        <div className="flex flex-col items-center justify-center gap-0.5 py-3 px-4 bg-muted">
          {canReorder ? (
            <>
              <button
                onClick={() => onReorder('up')}
                disabled={isFirst || isLocked}
                className={cn(
                  'h-5 w-5 rounded flex items-center justify-center transition-colors',
                  isFirst || isLocked
                    ? 'opacity-30 cursor-not-allowed'
                    : 'hover:bg-accent cursor-pointer'
                )}
                aria-label="Move up"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <span className="text-base font-semibold text-[var(--pm-cyan-text)]">{position}</span>
              <button
                onClick={() => onReorder('down')}
                disabled={isLast || isLocked}
                className={cn(
                  'h-5 w-5 rounded flex items-center justify-center transition-colors',
                  isLast || isLocked
                    ? 'opacity-30 cursor-not-allowed'
                    : 'hover:bg-accent cursor-pointer'
                )}
                aria-label="Move down"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </>
          ) : (
            <span className="text-base font-semibold text-[var(--pm-cyan-text)]">{position}</span>
          )}
        </div>

        {/* Column 2: Main content */}
        <div
          role="button"
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggle();
            }
          }}
          className="flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-muted/50 transition-colors min-w-0"
        >
          {/* Thumbnail with play overlay */}
          <div className="relative w-12 h-12 shrink-0">
            {clip.articleImageUrl ? (
              <img
                src={clip.articleImageUrl}
                alt=""
                className="w-12 h-12 rounded object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-12 h-12 rounded bg-muted flex items-center justify-center">
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              </div>
            )}
            {/* Play overlay on thumbnail */}
            {hasAudio && (
              <button
                onClick={handlePlayToggle}
                disabled={isLoadingAudio || !!audioError}
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded hover:bg-black/60 transition-colors disabled:opacity-50"
                aria-label={isPlayingProp ? 'Pause' : 'Play'}
              >
                {isLoadingAudio ? (
                  <Loader2 className="h-5 w-5 text-cyan-400 animate-spin" />
                ) : isPlayingProp ? (
                  <Pause className="h-5 w-5 text-cyan-400" />
                ) : (
                  <Play className="h-5 w-5 text-cyan-400 ml-0.5" />
                )}
              </button>
            )}
          </div>

          {/* Title + feed badge / inline audio player */}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm truncate">{clip.articleTitle}</h4>
            {/* Show AudioPlayer when playing OR when active in Play All mode (even if paused) */}
            {(isPlayingProp || isActiveInPlayAll) && audioUrl ? (
              <AudioPlayer
                src={audioUrl}
                autoPlay={isPlayingProp && !isActiveInPlayAll}
                isPlayingExternal={isActiveInPlayAll ? !isPlayAllPaused : undefined}
                onEnded={handleAudioEnded}
                onPause={() => onPlayStateChange?.(clip.id, false)}
                compact
              />
            ) : (
              <Badge variant="secondary" className="mt-1 text-xs font-normal">
                {clip.feedLabel || 'Unknown feed'}
              </Badge>
            )}
          </div>

          {/* Duration (only when has audio and not playing) */}
          {hasAudio && !isPlayingProp && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground shrink-0">
              <Clock className="h-3 w-3" />
              {formatDuration(clip.duration)}
            </div>
          )}

          {/* Status badge (hidden when playing) */}
          {!isPlayingProp && <StatusBadge status={clip.status} />}

          {/* Chevron */}
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              isExpanded && 'rotate-180'
            )}
          />
        </div>

        {/* Column 3: Delete button */}
        <div className="flex items-center pr-4">
          {canDelete && !isLocked && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteDialog(true);
              }}
              className="flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors shrink-0"
              aria-label="Delete clip"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded Content (outside grid to span full width) */}
      {isExpanded && (
        <div
          className="rounded-b-xl px-4 pb-4 pt-4 space-y-4 -mt-2"
          style={{
            backgroundColor: 'var(--pm-bg-card)',
            borderLeft: '1px solid var(--pm-border-default)',
            borderRight: '1px solid var(--pm-border-default)',
            borderBottom: '1px solid var(--pm-border-default)',
          }}
        >
          {/* View Original Article - always top left */}
          <Button variant="link" size="sm" className="h-auto p-0" asChild>
            <a
              href={clip.articleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs"
            >
              <ExternalLink className="h-3 w-3" />
              View Original Article
            </a>
          </Button>

          {/* Script Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h5 className="text-sm font-medium">Script</h5>
              {canEditScript && !isEditing && !isLocked && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleStartEdit}
                  disabled={!!actionInProgress}
                >
                  <Edit3 className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-2">
                <Textarea
                  value={editedScript}
                  onChange={(e) => setEditedScript(e.target.value)}
                  rows={8}
                  className="font-mono text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSaveScript}
                    disabled={actionInProgress === `save-script-${clip.id}`}
                  >
                    {actionInProgress === `save-script-${clip.id}` ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-1" />
                    )}
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={!!actionInProgress}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : hasScript ? (
              <pre
                className="text-sm p-3 rounded-lg whitespace-pre-wrap max-h-48 overflow-y-auto"
                style={{ backgroundColor: 'var(--pm-bg-base)', color: 'var(--pm-text-faint)' }}
              >
                {clip.script}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground">
                {clip.status === 'generating_script'
                  ? 'Script is being generated...'
                  : 'No script generated yet'}
              </p>
            )}
          </div>

          {/* Error display */}
          {clip.error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>{clip.error.step}:</strong> {clip.error.message}
              </AlertDescription>
            </Alert>
          )}

          {/* Action row with conditional regenerate buttons */}
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex gap-2">
              {canRegenerateScript && !isLocked && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRegenerateScriptDialog(true)}
                  disabled={!!actionInProgress}
                >
                  {actionInProgress === `regenerate-script-${clip.id}` ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-1" />
                  )}
                  Regenerate Script
                </Button>
              )}
              {canRegenerateAudio && !isLocked && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRegenerateAudioDialog(true)}
                  disabled={!!actionInProgress}
                >
                  {actionInProgress === `regenerate-audio-${clip.id}` ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-1" />
                  )}
                  Regenerate Audio
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Script Confirmation Dialog */}
      <Dialog open={showRegenerateScriptDialog} onOpenChange={setShowRegenerateScriptDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate Script?</DialogTitle>
            <DialogDescription>
              This will regenerate the script for this clip using the original article content.
              The existing script will be replaced.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegenerateScriptDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRegenerateScriptConfirm}>Regenerate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Audio Confirmation Dialog */}
      <Dialog open={showRegenerateAudioDialog} onOpenChange={setShowRegenerateAudioDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate Audio?</DialogTitle>
            <DialogDescription>
              This will regenerate the audio for this clip using the current script.
              The existing audio will be replaced.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegenerateAudioDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRegenerateAudioConfirm}>Regenerate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate After Edit Dialog */}
      <Dialog open={showRegenerateAfterEditDialog} onOpenChange={setShowRegenerateAfterEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate Audio with New Script?</DialogTitle>
            <DialogDescription>
              You've updated the script. Would you like to regenerate the audio to match the new
              script?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegenerateAfterEditDialog(false)}>
              Keep Current Audio
            </Button>
            <Button onClick={handleRegenerateAudioConfirm}>Regenerate Audio</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Clip Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent
          onKeyDown={(e) => {
            if (e.key === 'Enter' && actionInProgress !== `delete-clip-${clip.id}`) {
              e.preventDefault();
              handleDeleteConfirm();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Remove Clip</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this clip from the episode?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="font-medium">{clip.articleTitle}</p>
            <p className="text-sm text-muted-foreground">{clip.feedLabel || 'Unknown feed'}</p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={actionInProgress === `delete-clip-${clip.id}`}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={actionInProgress === `delete-clip-${clip.id}`}
            >
              {actionInProgress === `delete-clip-${clip.id}` ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove Clip'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
